import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { telegramChatsService } from '../../services/telegramChatsService';
import { TelegramChatResponse } from '../../types/telegramChats';
import './Page.css';

const PAGE_SIZE = 20;

const TelegramBotChats: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [chats, setChats] = useState<TelegramChatResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const currentPage = parseInt(searchParams.get('page') || '0', 10);

  const updatePage = (page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  const fetchTelegramChats = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await telegramChatsService.getTelegramChats({
        page: currentPage,
        pageSize: PAGE_SIZE,
      });

      if (response.success && response.data) {
        setChats(response.data.chats);
        setTotalCount(response.data.totalCount);
      } else {
        setError(response.error || t('clients.telegramChatsFetchFailed'));
      }
    } catch (error) {
      setError(t('clients.telegramChatsFetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, t]);

  useEffect(() => {
    fetchTelegramChats();
  }, [fetchTelegramChats]);

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderNullable = (value: string | null) => value || '-';

  const canGoNext = (currentPage + 1) * PAGE_SIZE < totalCount;

  return (
    <div className="page">
      <div className="page-header with-actions">
        <div>
          <h1>{t('clients.telegramChatsTitle')}</h1>
          <p>{t('clients.telegramChatsSubtitle')}</p>
        </div>
        <div className="page-header-actions">
          <button
            className="header-action-button secondary"
            onClick={() => navigate('/clients')}
          >
            {t('clients.backToClients')}
          </button>
        </div>
      </div>

      <div className="page-content">
        {!loading && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <span style={{ fontSize: '0.9rem', color: '#666' }}>{t('common.total')}</span>
              <span style={{ fontSize: '1.5rem', fontWeight: 'bold', marginLeft: '0.5rem' }}>{totalCount}</span>
            </div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              {t('common.page')} {currentPage + 1} {t('common.of')} {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="table-container">
          {loading ? (
            <div className="loading-container">{t('clients.loadingTelegramChats')}</div>
          ) : chats.length === 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('clients.clientId')}</th>
                  <th>{t('clients.telegramChatId')}</th>
                  <th>{t('clients.telegramUserId')}</th>
                  <th>{t('common.phone')}</th>
                  <th>{t('common.name')}</th>
                  <th>{t('clients.surname')}</th>
                  <th>{t('clients.username')}</th>
                  <th>{t('clients.isPremium')}</th>
                  <th>{t('clients.langCode')}</th>
                  <th>{t('clients.createdAt')}</th>
                  <th>{t('clients.updatedAt')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={11} className="no-data">
                    {t('clients.noTelegramChats')}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('clients.clientId')}</th>
                  <th>{t('clients.telegramChatId')}</th>
                  <th>{t('clients.telegramUserId')}</th>
                  <th>{t('common.phone')}</th>
                  <th>{t('common.name')}</th>
                  <th>{t('clients.surname')}</th>
                  <th>{t('clients.username')}</th>
                  <th>{t('clients.isPremium')}</th>
                  <th>{t('clients.langCode')}</th>
                  <th>{t('clients.createdAt')}</th>
                  <th>{t('clients.updatedAt')}</th>
                </tr>
              </thead>
              <tbody>
                {chats.map((chat) => (
                  <tr key={chat.id}>
                    <td>{chat.id}</td>
                    <td>{chat.chatId}</td>
                    <td>{chat.userId}</td>
                    <td>{renderNullable(chat.phone)}</td>
                    <td>{renderNullable(chat.name)}</td>
                    <td>{renderNullable(chat.surname)}</td>
                    <td>{renderNullable(chat.username)}</td>
                    <td>
                      {chat.isPremium === null ? '-' : chat.isPremium ? t('common.yes') : t('common.no')}
                    </td>
                    <td>{renderNullable(chat.langCode)}</td>
                    <td>{formatDate(chat.createdAt)}</td>
                    <td>{formatDate(chat.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && (
          <div className="pagination">
            <button
              className="pagination-button"
              onClick={() => updatePage(currentPage - 1)}
              disabled={currentPage === 0}
            >
              {t('common.previous')}
            </button>
            <span className="pagination-info">{t('common.page')} {currentPage + 1}</span>
            <button
              className="pagination-button"
              onClick={() => updatePage(currentPage + 1)}
              disabled={!canGoNext}
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TelegramBotChats;
